import { getStateCode } from '@utils/formattingUtils'

/** After save, scroll so success/snackbar is visible when address doctor is not shown. */
export function scrollWindowToTop(): void {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

/** Normalize address strings for comparison (address doctor suggested vs entered). */
function normPart(s: string | undefined | null): string {
  return (s ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
}

/**
 * Map Voyager/address-doctor state strings to a comparable key.
 * e.g. "AZ" and "AZ-AZ" (duplicate code) both become "AZ"; "Florida-FL" → "FL".
 */
function normStateProvinceForCompare(raw: string | null | undefined): string {
  const t = (raw ?? '').trim()
  if (!t) return ''

  const u = t.toUpperCase()
  if (u.length === 2 && /^[A-Z]{2}$/.test(u)) {
    return u
  }

  const m = t.match(/^(.+?)[\s\-–]+([A-Za-z]{2})\s*$/)
  if (m?.[2]) {
    const code = m[2].toUpperCase()
    const left = m[1].trim().toUpperCase()
    if (left.length === 2 && left === code) {
      return code
    }
    return code
  }

  return t.toLowerCase().replace(/\s+/g, ' ')
}

function stateProvinceMatches(
  a: string | undefined,
  b: string | undefined,
  statesProvinces: { name: string; value: string }[] | null | undefined,
): boolean {
  if (statesProvinces?.length) {
    const ca = getStateCode(statesProvinces, (a ?? '').trim())
    const cb = getStateCode(statesProvinces, (b ?? '').trim())
    if (ca && cb && ca === cb) {
      return true
    }
  }
  return normStateProvinceForCompare(a) === normStateProvinceForCompare(b)
}

/**
 * True when suggested and entered addresses are effectively the same (no meaningful choice for the user).
 * Pass `statesProvinces` from the countries API so full names (e.g. Arizona) match codes (e.g. AZ) after the profile fix.
 */
export function areAddressDoctorChoicesEquivalent(
  a: {
    addressLine1?: string
    addressLine2?: string
    addressLine3?: string
    city?: string
    stateProvince?: string
    postalCode?: string
    country?: string
  },
  b: typeof a,
  statesProvinces?: { name: string; value: string }[] | null,
): boolean {
  const keys = ['addressLine1', 'addressLine2', 'addressLine3', 'city', 'postalCode'] as const
  for (const k of keys) {
    if (normPart(a[k]) !== normPart(b[k])) {
      return false
    }
  }

  if (!stateProvinceMatches(a.stateProvince, b.stateProvince, statesProvinces)) {
    return false
  }
  const ca = normPart(a.country)
  const cb = normPart(b.country)
  if (ca !== '' || cb !== '') {
    return ca === cb
  }
  return true
}
